import { createContext, useContext, useEffect, useState } from "react";

/**
 * LanguageProvider — bilingue FR / EN pour le Guide du Voyageur.
 *
 * Portée : uniquement le Guide (les autres pages restent en français).
 * Persistance : localStorage (`cr-guide-lang`).
 * Défaut : "fr".
 *
 * Usage :
 *   const { lang, setLang, t } = useLanguage();
 *   t({ fr: "Bonjour", en: "Hello" })
 */

const LanguageContext = createContext(null);
const STORAGE_KEY = "cr-guide-lang";

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === "fr" || stored === "en") return stored;
    } catch (_e) { /* ignore */ }
    return "fr";
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (_e) { /* ignore */ }
  }, [lang]);

  const setLang = (next) => {
    if (next === "fr" || next === "en") setLangState(next);
  };

  /**
   * t — renvoie la version dans la langue courante.
   * - Si `input` est un objet { fr, en } → renvoie input[lang] (fallback FR).
   * - Si `input` est une string → la renvoie telle quelle.
   */
  const t = (input) => {
    if (input == null) return input;
    if (typeof input === "string" || typeof input === "number") return input;
    if (typeof input === "object" && (input.fr !== undefined || input.en !== undefined)) {
      return input[lang] ?? input.fr ?? input.en ?? "";
    }
    return input;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside <LanguageProvider>");
  return ctx;
}
